/**
 * Environment configuration
 * Provides a centralized way to access environment variables
 * Uses three strategies: window config, backend API, and Vite build-time env vars
 */

// Try to get config from window (set by backend if available)
const windowConfig = typeof window !== 'undefined' ? window.__APP_CONFIG__ : null;

// Runtime config loaded from backend
let runtimeConfig = null;

// Build-time values, read with STATIC import.meta.env.VITE_* accesses on purpose.
// A dynamic access (import.meta.env[someKey]) makes Vite inline the whole env
// object - every VITE_* variable present at build time - into the public bundle.
// Never add credentials or other secrets here (or anywhere under VITE_*).
const buildTimeEnv = {
  API_URL: import.meta.env.VITE_API_URL,
  COMPANY_FRONTEND_URL: import.meta.env.VITE_COMPANY_FRONTEND_URL,
  DEV_PORTAL_URL: import.meta.env.VITE_DEV_PORTAL_URL,
};

export const getEnvVariable = (key, fallback = '') => {
  // First, try window config (set by backend at runtime)
  if (windowConfig && windowConfig[key]) {
    return windowConfig[key];
  }

  // Then, try runtime config (fetched from /api/env)
  if (runtimeConfig && runtimeConfig[key]) {
    return runtimeConfig[key];
  }

  // Then, try the build-time values (resolved by Vite)
  const value = buildTimeEnv[key];
  if (value) {
    return value;
  }

  // Finally, use fallback
  return fallback;
};

// Load config from backend API
async function loadRuntimeConfig() {
  try {
    const apiUrl = getEnvVariable('API_URL', 'http://localhost:8000');
    const response = await fetch(`${apiUrl}/api/env`);
    if (response.ok) {
      runtimeConfig = await response.json();
      console.log('[AppConfig] Loaded from backend:', runtimeConfig);
    }
  } catch (error) {
    console.warn('Failed to load runtime config from backend:', error);
  }
}

// Initialize config - load from backend if available
let configPromise = loadRuntimeConfig();

// Create config object with dynamic getters so values update after loading
export const config = {
  get apiUrl() {
    return getEnvVariable('API_URL', 'http://localhost:8000');
  },
  get companyFrontendUrl() {
    return getEnvVariable('COMPANY_FRONTEND_URL', '');
  },
  get devPortalUrl() {
    return getEnvVariable('DEV_PORTAL_URL', '');
  },
};

// Expose a function to ensure config is loaded before use
export const ensureConfigLoaded = () => configPromise;

export default config;
